import { useState } from "react";

const TEMPLATE = `## What's this about
Got someone we have to interview? On a topic that needs a whole episode?

## Anything else
- links, dates, references`;

const TOOLBAR_ACTIONS = ["B", "I", "<>", "+", "-", "[]", "{}"];

// Replace with your Formspree endpoint from formspree.io (Form > Integration tab).
const FORMSPREE_ENDPOINT =
  process.env.REACT_APP_FORMSPREE_ENDPOINT ||
  "https://formspree.io/f/YOUR_FORM_ID";

interface ContactFormProps {
  selectedLabel: string;
}

export default function ContactForm({ selectedLabel }: ContactFormProps) {
  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState(TEMPLATE);
  const [honeypot, setHoneypot] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charCount = body.length;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Honeypot: real users never see/fill this field. If it's populated, a bot
    // submitted the form — silently pretend success and skip the network call.
    if (honeypot) {
      setSubmitted(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          title,
          name,
          email,
          label: selectedLabel,
          message: body,
          _subject: `${title} — ${selectedLabel}`,
          _replyto: email,
          _gotcha: honeypot,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.errors?.[0]?.message || `Submission failed (${res.status})`,
        );
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        className="border border-stone-200 rounded p-10 text-center"
        style={{ backgroundColor: "#faf9f4" }}
      >
        <p className="font-playfair text-stone-800 text-[30px] mb-2">
          PR submitted.
        </p>
        <p className="font-montserrat text-stone-500 text-[18px]">
          We'll review and merge back soon. Thanks for sliding into the inbox.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Honeypot — hidden from real users, catches bots. Not display:none so
          naive bots still fill it; kept out of the tab order and a11y tree. */}
      <input
        type="text"
        name="_gotcha"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: "1px",
          height: "1px",
          opacity: 0,
        }}
      />

      {/* Title */}
      <div>
        <label
          className="inline-block font-montserrat text-burgundy rounded mb-1.5 px-2 py-0.5"
          style={{
            fontSize: "13px",
            letterSpacing: "0.12em",
            backgroundColor: "#f0c8cc",
          }}
        >
          ADD A TITLE
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Pitch a guest / topic"
          className="w-full border border-stone-200 rounded px-3 py-2.5 font-montserrat text-stone-700 text-[18px] placeholder-stone-300 focus:outline-none focus:border-burgundy"
          style={{
            backgroundColor: "#faf9f4",
            transition: "border-color 0.15s ease",
          }}
          required
        />
      </div>

      {/* Name + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            className="inline-block font-montserrat text-burgundy rounded mb-1.5 px-2 py-0.5"
            style={{
              fontSize: "13px",
              letterSpacing: "0.12em",
              backgroundColor: "#f0c8cc",
            }}
          >
            {"// NAME"}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="@your-handle"
            className="w-full border border-stone-200 rounded px-3 py-2.5 font-montserrat text-stone-700 text-[18px] placeholder-stone-300 focus:outline-none focus:border-burgundy"
            style={{
              backgroundColor: "#faf9f4",
              transition: "border-color 0.15s ease",
            }}
            required
          />
        </div>
        <div>
          <label
            className="inline-block font-montserrat text-burgundy rounded mb-1.5 px-2 py-0.5"
            style={{
              fontSize: "13px",
              letterSpacing: "0.12em",
              backgroundColor: "#f0c8cc",
            }}
          >
            {"// EMAIL"}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@somewhere.dev"
            className="w-full border border-stone-200 rounded px-3 py-2.5 font-montserrat text-stone-700 text-[18px] placeholder-stone-300 focus:outline-none focus:border-burgundy"
            style={{
              backgroundColor: "#faf9f4",
              transition: "border-color 0.15s ease",
            }}
            required
          />
        </div>
      </div>

      {/* Body */}
      <div>
        {/* Toolbar */}
        <div
          className="flex items-center gap-1 border border-stone-200 border-b-0 rounded-t px-2 py-1.5"
          style={{ backgroundColor: "#f0efe8" }}
        >
          {TOOLBAR_ACTIONS.map((action) => (
            <button
              key={action}
              type="button"
              className="font-montserrat text-stone-500 hover:text-stone-800 px-1.5 py-0.5 rounded hover:bg-stone-200 focus-visible:outline-none"
              style={{
                fontSize: "14px",
                fontWeight: 600,
                transition: "all 0.15s ease",
              }}
            >
              {action}
            </button>
          ))}
        </div>

        {/* Textarea */}
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className="w-full border border-stone-200 px-3 py-3 font-montserrat text-stone-600 text-[18px] focus:outline-none focus:border-burgundy resize-none"
          style={{
            backgroundColor: "#faf9f4",
            lineHeight: "1.7",
            transition: "border-color 0.15s ease",
          }}
        />

        <p
          className="font-montserrat text-stone-500 mt-1.5 text-right"
          style={{ fontSize: "14px", letterSpacing: "0.06em" }}
        >
          Markdown supported · {charCount} chars
        </p>
      </div>

      {/* Submit */}
      <div className="flex flex-col items-end gap-2">
        {error && (
          <p
            className="font-montserrat text-burgundy"
            style={{ fontSize: "13px" }}
            role="alert"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="font-montserrat font-semibold text-cream bg-burgundy hover:opacity-90 hover:-translate-y-0.5 active:translate-y-0 active:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burgundy focus-visible:ring-offset-2 rounded px-6 py-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
          style={{
            fontSize: "15px",
            letterSpacing: "0.14em",
            transition: "opacity 0.2s ease, transform 0.2s ease",
          }}
        >
          {submitting ? "SUBMITTING…" : "CREATE PULL REQUEST →"}
        </button>
      </div>
    </form>
  );
}
